/* RelliBaby native section behaviours: header offset, tabs, carousels, endless strips, lazy video,
   "Buy it now" label. Vanilla, no libraries, works in the theme editor (re-inits on section load). */
(() => {
  /* ---- fixed header height -> --rb-header-h ---- */
  const header = document.querySelector('.header-wrapper');
  if (header) {
    const setH = () => document.documentElement.style.setProperty('--rb-header-h', `${header.offsetHeight}px`);
    setH();
    if ('ResizeObserver' in window) new ResizeObserver(setH).observe(header);
  }

  /* ---- announcement bar: sits above the fixed header, which slides up as the bar scrolls away ---- */
  const bar = document.querySelector('.announcement-bar-section');
  if (bar && header) {
    const rootStyle = document.documentElement.style;
    let barH = 0;
    let ticking = false;
    // ShoppingIL (Nov 2026): the event strip (.rb-sil) is pinned to the top, so the header stays right under it
    const pinned = () => !!bar.querySelector('.rb-sil');
    const place = () => {
      ticking = false;
      rootStyle.setProperty('--rb-announce-offset', `${pinned() ? barH : Math.max(0, barH - window.scrollY)}px`);
    };
    const measureBar = () => {
      barH = bar.offsetHeight;
      rootStyle.setProperty('--rb-announce-h', `${barH}px`);
      place();
    };
    measureBar();
    if ('ResizeObserver' in window) new ResizeObserver(measureBar).observe(bar);
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(place); }
    }, { passive: true });
  }

  /* ---- tabs: [data-rb-tabs] > [role=tab][aria-controls] ---- */
  const initTabs = (root) => {
    root.querySelectorAll('[data-rb-tabs]').forEach((group) => {
      if (group.dataset.rbReady) return;
      group.dataset.rbReady = '1';
      const tabs = [...group.querySelectorAll('[role="tab"]')];
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on);
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (focus) tab.focus();
        group.dispatchEvent(new CustomEvent('rb:tab-change', { bubbles: true }));
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          const rtl = getComputedStyle(group).direction === 'rtl';
          const step = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[e.key];
          if (!step) return;
          e.preventDefault();
          select(tabs[(i + step + tabs.length) % tabs.length], true);
        });
      });
      group._rbSelect = (id) => {
        const t = tabs.find((x) => x.dataset.blockId === id || x.getAttribute('aria-controls') === id);
        if (t) select(t);
      };
    });
  };

  /* ---- carousels: [data-rb-carousel] > .rb-track > slides ---- */
  const desktopMq = window.matchMedia('(min-width: 990px)');
  const initCarousels = (root) => {
    root.querySelectorAll('[data-rb-carousel]').forEach((wrap) => {
      if (wrap.dataset.rbReady) return;
      wrap.dataset.rbReady = '1';
      const track = wrap.querySelector('.rb-track');
      const prev = wrap.querySelector('.rb-arrow--prev');
      const next = wrap.querySelector('.rb-arrow--next');
      const sib = wrap.nextElementSibling;
      const dotsWrap = wrap.querySelector('.rb-dots') || (sib && sib.classList.contains('rb-dots') ? sib : null);
      const label = wrap.dataset.rbSlideLabel || 'Slide';
      let slides = [];
      let dots = [];
      const visible = new Set();
      const update = () => {
        const first = slides.findIndex((s) => visible.has(s));
        dots.forEach((d, i) => d.setAttribute('aria-current', i === first));
        if (prev) prev.disabled = visible.has(slides[0]);
        if (next) next.disabled = visible.has(slides[slides.length - 1]);
      };
      // Only displayed slides count: cards can be desktop-only or mobile-only (rb-vis--*)
      const build = () => {
        slides = [...track.children].filter((s) => getComputedStyle(s).display !== 'none');
        dots = [];
        if (dotsWrap) {
          dotsWrap.innerHTML = '';
          if (slides.length > 1) {
            slides.forEach((s, i) => {
              const b = document.createElement('button');
              b.type = 'button';
              b.setAttribute('aria-label', `${label} ${i + 1}`);
              b.addEventListener('click', () => s.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' }));
              dotsWrap.appendChild(b);
              dots.push(b);
            });
          }
        }
        update();
      };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => (en.intersectionRatio >= 0.6 ? visible.add(en.target) : visible.delete(en.target)));
        update();
      }, { root: track, threshold: [0, 0.6, 1] });
      [...track.children].forEach((s) => io.observe(s));
      build();
      desktopMq.addEventListener('change', build);
      const page = (dir) => {
        const rtl = getComputedStyle(track).direction === 'rtl';
        track.scrollBy({ left: dir * (rtl ? -1 : 1) * track.clientWidth, behavior: 'smooth' });
      };
      prev && prev.addEventListener('click', () => page(-1));
      next && next.addEventListener('click', () => page(1));
    });
  };

  /* ---- endless strip: [data-rb-loop] (a scrolling list) gets a copy of its items on each side; when a swipe
     ends inside a copy, the list jumps by exactly one set, so it never runs out. Copies are hidden from
     screen readers and keyboard. Only runs while the items overflow (phones), so desktop is untouched. ---- */
  const initLoops = (root) => {
    root.querySelectorAll('[data-rb-loop]').forEach((list) => {
      if (list.dataset.rbReady) return;
      list.dataset.rbReady = '1';
      const items = [...list.children];
      if (items.length < 2) return;
      let looped = false;
      let setWidth = 0;
      const rtl = () => getComputedStyle(list).direction === 'rtl';
      const pos = () => Math.abs(list.scrollLeft);   // distance from the start edge, LTR or RTL
      const jumpTo = (d) => { list.scrollLeft = rtl() ? -d : d; };
      const copies = () => items.map((li) => {
        const c = li.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.dataset.rbClone = '';
        c.removeAttribute('data-shopify-editor-block');
        c.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        return c;
      });
      const measure = () => { setWidth = Math.abs(items[0].offsetLeft - list.children[0].offsetLeft); };
      const recenter = () => {
        if (!looped || !setWidth) return;
        const d = pos();
        if (d < setWidth * 0.5) jumpTo(d + setWidth);
        else if (d > setWidth * 1.5) jumpTo(d - setWidth);
      };
      const setup = () => {
        if (!list.clientWidth) return;                                   // hidden on this device
        if (!looped) {
          if (list.scrollWidth <= list.clientWidth + 1) return;          // everything fits: no loop needed
          list.prepend(...copies());
          list.append(...copies());
          looped = true;
          measure();
          jumpTo(setWidth);
        } else {
          const offset = setWidth ? pos() % setWidth : 0;
          measure();
          jumpTo(setWidth + offset);
        }
      };
      let timer;
      list.addEventListener('scroll', () => { clearTimeout(timer); timer = setTimeout(recenter, 140); }, { passive: true });
      if ('onscrollend' in window) list.addEventListener('scrollend', recenter);
      setup();
      if ('ResizeObserver' in window) new ResizeObserver(() => setup()).observe(list);   // rotation, or shown later
    });
  };

  /* ---- "Buy it now": Shopify draws this button in the browser with its own wording, which no theme
     translation reaches, so relabel it with products.product.rb_buy_now (Edit default theme content) ---- */
  const buyNow = window.rbStrings && window.rbStrings.buyNow;
  const initBuyNow = (root) => {
    if (!buyNow) return;
    root.querySelectorAll('.shopify-payment-button').forEach((box) => {
      if (box.dataset.rbReady) return;
      box.dataset.rbReady = '1';
      const relabel = () => box.querySelectorAll('.shopify-payment-button__button--unbranded').forEach((b) => {
        if (b.textContent !== buyNow) b.textContent = buyNow;
      });
      relabel();
      new MutationObserver(relabel).observe(box, { childList: true, subtree: true });
    });
  };

  /* ---- silent looping video, like a GIF: <video data-rb-lazy><source data-src> + .rb-video-toggle ----
     Loads when it comes near the screen and plays on a loop while on screen (the browser's own muted autoplay, which
     phones resume reliably; play() is repeated whenever it is on screen). It can never make a sound: always muted,
     re-muted if anything unmutes it, no controls, no picture-in-picture. The round button (or a tap on the video)
     pauses and plays it; where autoplay is refused (iPhone Low Power Mode) or the visitor asked for reduced motion,
     the video waits on its first frame with the play button showing. */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initVideos = (root) => {
    const vids = root.querySelectorAll('video[data-rb-lazy]:not([data-rb-ready])');
    if (!vids.length) return;
    const silence = (v) => { v.muted = true; v.defaultMuted = true; v.volume = 0; };
    const load = (v) => {
      const sources = v.querySelectorAll('source[data-src]');
      if (!sources.length) return;
      sources.forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
      v.load();
    };
    const start = (v) => {
      if (v.dataset.rbStopped || !v.paused) return;
      silence(v);
      const p = v.play();
      if (p && p.catch) p.catch(() => {});   // autoplay refused: the play button stays visible
    };
    const loader = new IntersectionObserver((entries, obs) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        load(en.target);
        obs.unobserve(en.target);
      });
    }, { rootMargin: '300px 0px' });
    const player = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const v = en.target;
        if (en.isIntersecting) { load(v); start(v); }   // load() too, in case it scrolled in before the loader fired
        else if (!v.paused) v.pause();                  // off screen: save battery and data
      });
    }, { threshold: 0.25 });
    vids.forEach((v) => {
      v.dataset.rbReady = '1';
      silence(v);
      v.controls = false;
      v.playsInline = true;
      v.disablePictureInPicture = true;
      v.disableRemotePlayback = true;
      v.addEventListener('volumechange', () => { if (!v.muted || v.volume) silence(v); });
      const btn = v.parentElement && v.parentElement.querySelector('.rb-video-toggle');
      const sync = () => {
        if (!btn) return;
        btn.classList.toggle('is-playing', !v.paused);
        btn.setAttribute('aria-label', v.paused ? btn.dataset.labelPlay : btn.dataset.labelPause);
      };
      const toggle = () => {
        if (v.paused) { delete v.dataset.rbStopped; load(v); start(v); }
        else { v.dataset.rbStopped = '1'; v.pause(); }
      };
      v.addEventListener('play', sync);
      v.addEventListener('pause', sync);
      v.addEventListener('click', toggle);
      if (btn) { btn.hidden = false; btn.addEventListener('click', toggle); sync(); }
      // reduced motion, or "stop animations" in the accessibility menu (assets/rb-a11y.js)
      if (reduceMotion.matches || document.documentElement.classList.contains('rb-a11y-motion')) v.dataset.rbStopped = '1';
      else v.autoplay = true;
      // Sprint 1 (Oct 20, 2026): a first-screen video ([data-rb-after-load], sections/rb-hero-baby) starts downloading only
      // after window "load", so it never competes with the LCP photo underneath it.
      const watch = () => { loader.observe(v); player.observe(v); };
      if (v.closest('[data-rb-after-load]') && document.readyState !== 'complete') window.addEventListener('load', watch, { once: true });
      else watch();
    });
  };

  /* ---- product page: first accordion open, as on the EComposer pages ---- */
  const firstAccordion = document.querySelector('.template-product .product__accordion details');
  if (firstAccordion) firstAccordion.open = true;

  const init = (root = document) => { initTabs(root); initCarousels(root); initVideos(root); initLoops(root); initBuyNow(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  /* theme editor */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    if (e.target.tagName === 'DETAILS') e.target.open = true;   // FAQ question picked in the editor
    const group = e.target.closest('[data-rb-tabs]');
    if (!group || !group._rbSelect) return;
    const panel = e.target.closest('[role="tabpanel"]');
    group._rbSelect(panel ? panel.id : e.detail.blockId);
  });
})();
